import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signUp, checkSociete, login } from '../Services';
import { initOnboardingLocal } from '../onboarding';
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from './SnackbarProvider';

type Groupe = {
  id: number;
  id_societe: number;
  nom: string;
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    backgroundColor: '#f8f9fa',
    '&:hover': { backgroundColor: '#f0f1f2' },
    '&.Mui-focused': { backgroundColor: 'white' },
  },
};

const SignUpScreen = () => {
  const [nom, setNom] = useState('');
  const [isNomDisabled, setIsNomDisabled] = useState(false);
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordStrengthColor, setPasswordStrengthColor] = useState('#e53935');
  const [passwordHelperText, setPasswordHelperText] = useState(
    'Le mot de passe doit contenir au moins 8 caractères, une majuscule, un chiffre et un caractère spécial.'
  );
  const [groupes, setGroupes] = useState<Groupe[]>([]);
  const [selectedGroupe, setSelectedGroupe] = useState<number | string>('');
  const [idSociete, setIdSociete] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { showSnackbar } = useSnackbar();

  const isPasswordSecure = (password: string) => password.length >= 8;
  const isEmailValid = (emailVal: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal);
  const isPhoneValid = (phone: string) => /^[0-9]+$/.test(phone);

  const getPasswordStrength = (password: string) => {
    const length = password.length;
    if (length >= 8) {
      setPasswordHelperText('Mot de passe fort');
      return '#2e7d32';
    }
    if (length >= 4) {
      setPasswordHelperText('Mot de passe moyen');
      return '#ff9800';
    }
    setPasswordHelperText('Le mot de passe doit contenir au moins 8 caractères.');
    return '#e53935';
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setMotDePasse(password);
    setPasswordStrengthColor(getPasswordStrength(password));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (isNomDisabled) {
      setNom('');
      setIsNomDisabled(false);
      setGroupes([]);
      setIdSociete(null);
      setSelectedGroupe('');
    }
  };

  const handleCheckSociete = async () => {
    if (email && email.includes('@')) {
      const domain = email.split('@')[1];
      try {
        const response = await checkSociete(domain);
        if (response.societe) {
          setNom(response.societe.nom);
          setIsNomDisabled(true);
          setIdSociete(response.societe.id);
        }
        if (response.groupes && response.groupes.length > 0) {
          setGroupes(response.groupes);
          if (response.groupes.length === 1) {
            setSelectedGroupe(response.groupes[0].id);
          }
        }
      } catch (error) {
        console.error('Erreur lors du check de société', error);
      }
    }
  };

  const handleSignUp = async () => {
    if (isLoading) return;
    if (!nom.trim() || !email.trim() || !motDePasse.trim() || !telephone.trim()) {
      showSnackbar('Veuillez remplir tous les champs.', 'error');
      return;
    }
    if (!isEmailValid(email)) {
      showSnackbar('Veuillez entrer un email valide.', 'error');
      return;
    }
    if (email !== confirmEmail) {
      showSnackbar('Les adresses email ne correspondent pas.', 'error');
      return;
    }
    if (!isPhoneValid(telephone)) {
      showSnackbar('Veuillez entrer un numéro de téléphone valide.', 'error');
      return;
    }
    if (!isPasswordSecure(motDePasse)) {
      showSnackbar('Le mot de passe doit contenir au moins 8 caractères.', 'error');
      return;
    }
    if (idSociete && groupes.length > 0 && !selectedGroupe) {
      showSnackbar('Veuillez sélectionner un groupe.', 'error');
      return;
    }
    setIsLoading(true);

    // On ne peut pas conclure du seul statut HTTP que le compte n'existe pas :
    // le backend renvoie aussi 500 quand l'envoi du mail de bienvenue échoue
    // ALORS QUE l'INSERT est passé, et 500 également quand l'INSERT lui-même échoue.
    // Seule la connexion qui suit tranche réellement.
    let compteCree = false;
    try {
      await signUp({
        nom,
        email,
        motDePasse,
        telephone,
        idSociete: idSociete ? idSociete : undefined,
        idGroupe: selectedGroupe ? selectedGroupe : undefined,
      });
      compteCree = true;
    } catch (error: any) {
      if (error?.response?.status === 400) {
        setIsLoading(false);
        showSnackbar('Un compte existe déjà avec cette adresse email.', 'error');
        return;
      }
      compteCree = false;
    }

    // Connexion automatique. C'est la correction centrale : l'inscription faisait
    // `navigate(-1)`, renvoyant l'utilisateur ressaisir les identifiants qu'il
    // venait de choisir — et, s'il avait ouvert /signup directement, hors de l'application.
    try {
      const user = await login(email, motDePasse);
      if (!user || !user.id_utilisateur) throw new Error('Réponse de connexion inexploitable.');

      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('mail', email);
      // Le carrousel de 5 images distantes de MyTabs ne doit pas s'ouvrir par-dessus
      // l'arrivée dans l'application. Le lot 4 le remplace par un écran de bienvenue natif.
      localStorage.setItem('hasShownModal', 'true');
      initOnboardingLocal(user.id_utilisateur);

      setIsLoading(false);
      showSnackbar('Bienvenue chez Visamonde. Votre compte est prêt.', 'success');
      navigate('/retour', { replace: true });
    } catch (erreurConnexion) {
      setIsLoading(false);
      if (compteCree) {
        showSnackbar(
          "Votre compte est créé, mais la connexion automatique n'a pas abouti. Connectez-vous ci-dessous.",
          'warning'
        );
      } else {
        showSnackbar(
          "Nous n'avons pas pu confirmer la création du compte. Essayez de vous connecter ; si cela échoue, recommencez l'inscription.",
          'warning'
        );
      }
      navigate('/login', { replace: true, state: { emailPreRempli: email } });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          maxWidth: isDesktop ? 900 : 420,
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          backgroundColor: 'white',
        }}
      >
        {/* Left panel - branding (desktop only) */}
        {isDesktop && (
          <Box
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 5,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.06)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -40,
                left: -40,
                width: 160,
                height: 160,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.04)',
              }}
            />

            <Box
              component="img"
              src="/logoblanc.png"
              alt="Visamonde"
              sx={{ width: 220, height: 'auto', mb: 4, objectFit: 'contain' }}
            />
            <Typography
              variant="h5"
              sx={{ color: 'white', fontWeight: 700, textAlign: 'center', mb: 1.5, lineHeight: 1.3 }}
            >
              Rejoignez Visamonde
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.75)', textAlign: 'center', maxWidth: 280, lineHeight: 1.6 }}
            >
              Créez votre compte pour gérer vos demandes de visa en toute simplicité.
            </Typography>
          </Box>
        )}

        {/* Right panel - form */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflowY: 'auto',
            maxHeight: '95vh',
          }}
        >
          {/* Back button + Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                backgroundColor: '#f5f5f5',
                '&:hover': { backgroundColor: '#eee' },
                width: 40,
                height: 40,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20, color: '#555' }} />
            </IconButton>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
                Création de compte
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', fontSize: '0.8rem' }}>
                Remplissez le formulaire ci-dessous
              </Typography>
            </Box>
          </Box>

          {/* Mobile logo */}
          {!isDesktop && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box
                component="img"
                src="/logo.png"
                alt="Logo"
                sx={{ width: 160, height: 'auto', mx: 'auto', objectFit: 'contain' }}
              />
            </Box>
          )}

          <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Nom */}
            <TextField
              fullWidth
              label="Nom Prénom / Raison sociale"
              variant="outlined"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              disabled={isNomDisabled}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ color: '#999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />

            {/* Email */}
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleCheckSociete}
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />

            {/* Confirm email */}
            {email && (
              <TextField
                fullWidth
                label="Confirmer l'Email"
                variant="outlined"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />
            )}

            {/* Groupe select */}
            {groupes.length > 0 && (
              <FormControl fullWidth sx={inputSx}>
                <InputLabel id="groupe-select-label">Groupe</InputLabel>
                <Select
                  labelId="groupe-select-label"
                  value={selectedGroupe}
                  label="Groupe"
                  onChange={(e) => setSelectedGroupe(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <GroupsOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  }
                  sx={{ borderRadius: 3 }}
                >
                  {groupes.map((groupe) => (
                    <MenuItem key={groupe.id} value={groupe.id}>
                      {groupe.nom}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Telephone */}
            <TextField
              fullWidth
              label="Téléphone"
              variant="outlined"
              value={telephone}
              onChange={(e) => setTelephone(e.target.value)}
              type="tel"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />

            {/* Password */}
            <TextField
              fullWidth
              label="Mot de passe"
              variant="outlined"
              value={motDePasse}
              onChange={handlePasswordChange}
              type={passwordVisible ? 'text' : 'password'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setPasswordVisible(!passwordVisible)} edge="end" size="small">
                      {passwordVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />

            {/* Password strength */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#eee',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: motDePasse.length >= 8 ? '100%' : motDePasse.length >= 4 ? '60%' : motDePasse.length > 0 ? '25%' : '0%',
                    height: '100%',
                    borderRadius: 3,
                    backgroundColor: passwordStrengthColor,
                    transition: 'all 0.3s ease',
                  }}
                />
              </Box>
            </Box>
            <Typography variant="caption" sx={{ color: '#888', mt: -0.5 }}>
              {passwordHelperText}
            </Typography>

            {/* Submit */}
            <Button
              variant="contained"
              fullWidth
              onClick={handleSignUp}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                mt: 1,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1b5e20 0%, #145218 100%)',
                  boxShadow: '0 6px 20px rgba(46,125,50,0.4)',
                },
              }}
            >
              {isLoading ? 'Création en cours…' : 'Créer mon compte'}
            </Button>

            {/* Back link */}
            <Button
              variant="text"
              fullWidth
              onClick={() => navigate(-1)}
              disabled={isLoading}
              sx={{
                textTransform: 'none',
                color: '#888',
                fontWeight: 500,
                '&:hover': { color: '#555', backgroundColor: 'transparent' },
              }}
            >
              Revenir à la connexion
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignUpScreen;
